class CommonRequestService {

    constructor(
        $http,
        appConfig
    ) {
        'ngInject';

        this.$http = $http;
        this.appConfig = appConfig;
    }

    /***
     * Get a list of campuses that belong to the given client.
     *
     * @param clientId   the ID of the client whose campuses the user is fetching.
     * @param params     optional query params to pass to the request.
     *
     * @returns {Promise|Array}
     */
    getCampusesByClient(clientId, params) {
        const config = params ? {params: params} : {};
        return this.$http.get(`${this.appConfig.apiUrl}/clients/${clientId}/campuses/`, config);
    }

    /***
     * Grab the list of campuses that belong to the given client and country combination.
     *
     * @param clientId      the ID of the client to filter the campus list.
     * @param countryId     the ID of the country to filter the campus list.
     * @param params     optional query params to pass to the request.
     */
    getCampusesByClientAndCountry(clientId, countryId, params={}) {
        const config = {
            params: {
                country: countryId
            }
        };

        for (let attrName in params) {
            config.params[attrName] = params[attrName];
        }

        return this.$http.get(`${this.appConfig.apiUrl}/clients/${clientId}/campuses/`, config);
    }

    /***
     * Get details about a specific campus.
     *
     * @param campusId      the ID of the campus being fetched.
     */
    getCampusDetails(campusId) {
        return this.$http.get(`${this.appConfig.apiUrl}/campuses/${campusId}/`)
            .then((successResponse) => successResponse.data);
    }

    /***
     * Get a list of every client.
     *
     * @param params    optional query params to pass to the request.
     *
     * @returns {Promise|Array}
     */
    getClients(params) {
        const config = params ? {params: params} : {};
        return this.$http.get(this.appConfig.apiUrl + '/clients/', config)
        .then((successResponse) =>  successResponse.data);
    }

    /***
     * Get details about a specific client.
     *
     * @param clientId      the ID of the client being fetched.
     */
    getClient(clientId) {
        return this.$http.get(`${this.appConfig.apiUrl}/clients/${clientId}/`)
            .then((successResponse) => {
                return successResponse.data;
            })
    }

    /***
     * Grab the list of countries that contain a campus belonging to the given client.
     *
     * For example, if client A has 2 campuses, C1 (in Canada) and C2 (in Brazil), when client A
     * is selected, a list containing [Canada, Brazil] is returned.
     *
     * @param clientId  the ID of the client to filter the country list.
     * @param params    optional query params to pass to the request.
     *
     * @returns {Promise}
     */
    getCountriesByClient(clientId, params) {
        const config = params ? {params: params} : {};
        return this.$http.get(`${this.appConfig.apiUrl}/clients/${clientId}/countries/`, config);
    }

    /***
     * Grab the list of facilities that belong to the given campus.
     *
     * @param campusId      the ID of the campus to filter the facility list.
     * @param params    optional query params to pass to the request.
     *
     * @returns {Promise}
     */
    getFacilitiesByCampus(campusId, params) {
        const config = params ? {params: params} : {};
        return this.$http.get(`${this.appConfig.apiUrl}/campuses/${campusId}/facilities/`, config)
    }

    /***
     * Get details about a specific facility.
     *
     * @param facilityId      the ID of the facility being fetched.
     */
    getFacilityDetails(facilityId) {
        return this.$http.get(`${this.appConfig.apiUrl}/facilities/${facilityId}/`)
            .then((successResponse) => successResponse.data);
    }

    /***
     * Create a list of objects containing an id and name for each month in the year.
     *
     * @returns {Array}
     */
    getMonthList() {
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
        ];
        const monthList = [];

        for (let i = 1; i <= 12; i++) {
            monthList.push({
                id: i,
                name: months[i-1]
            })
        }

        return monthList;
    }

    /***
     * Create a list of objects with one for each year from min year or 2016 until the max year or current year.
     *
     * @returns {Array}
     */
    getYearList(minYear, maxYear) {
        if (minYear === undefined) {
            minYear = 2016;
        }
        if (maxYear === undefined)
            maxYear = new Date().getFullYear();

        const yearList = [];

        for (let i = minYear; i <= maxYear; i++) {
            yearList.push({
                name: i
            })
        }

        return yearList;
    }

    /***
     * Get a list of all practitioners.
     */
    getPractitioners() {
        return this.$http.get(`${this.appConfig.apiUrl}/practitioners/`)
            .then((successResponse) =>  successResponse.data);
    }

    /***
     * Get a list of all practitioners that are assigned to a given facility.
     *
     * @param facilityId    thie ID of the facility whose practitioners are being fetched.
     */
    getPractitionersByFacility(facilityId) {
        const config = {
            params: {
                facility: facilityId
            }
        };
        return this.$http.get(`${this.appConfig.apiUrl}/practitioners/`, config)
            .then((successResponse) => successResponse.data);
    }

    /***
     * Get a list of every client that has surveys.
     *
     * @returns {Promise|Array}
     */
    getSurveyClients() {
        const config = {
            params: {
                can_create_surveys: 1
            }
        };
        return this.$http.get(this.appConfig.apiUrl + '/clients/', config)
        .then((successResponse) => {
               return successResponse.data;
            });
    }

    /***
     * Update an existing PractitionerFacility assignment (either enabling or disabling the assignment)
     *
     * @param practitionerFacilitiesId      the ID of the PractitionerFacilities assignment row
     * @param data                          an object containing the PractitionerFacilities parameters to update
     */
    updatePractitionerFacilityAssignment(practitionerFacilitiesId, data) {
        return this.$http.patch(
            `${this.appConfig.apiUrl}/practitioner-facilities/${practitionerFacilitiesId}/`, data)
            .then((successResponse) =>  successResponse.data);
    }

    /***
     * Get a list of all user roles.
     */
    getPermissions() {
        return this.$http.get(`${this.appConfig.apiUrl}/user-permissions/`)
            .then((successResponse) => successResponse.data);
    }

    /***
     * Get a list of all user roles.
     */
    getRoles() {
        return this.$http.get(`${this.appConfig.apiUrl}/user-roles/`)
            .then((successResponse) => successResponse.data);
    }

}

export default CommonRequestService;


// WEBPACK FOOTER //
// ./app/common/common-request/common-request-service.js